import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import { useParams, useNavigate } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useEvents } from '../contexts/EventContext';
import { useSport, sportTypes } from '../contexts/SportContext';
import { useNotification } from '../contexts/NotificationContext';
import EventCreationModal from '../components/EventCreationModal';
import MyEventsModal from '../components/MyEventsModal';
import EventDetails from '../components/EventDetails';
import ChangeVibeModal from '../components/ChangeVibeModal';
import NotificationPrompt from '../components/NotificationPrompt';
import { motion, AnimatePresence } from 'framer-motion';
import './MapPage.css';
import { adItems } from '../config/ads.js';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png', iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png', shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png' });

const FlyToController = ({ event }) => {
    const map = useMap();
    useEffect(() => {
        if (event) {
            map.flyTo([event.latitude, event.longitude], 15, { animate: true, duration: 1.5 });
            const timerId = setTimeout(() => {
                map.eachLayer((layer) => {
                    if (layer.options.eventId === event.id) {
                        const markerElement = layer.getElement();
                        if (markerElement) {
                            markerElement.classList.add('pulse-animation');
                            setTimeout(() => markerElement.classList.remove('pulse-animation'), 1200);
                        }
                    }
                });
            }, 1500);
            return () => clearTimeout(timerId);
        }
    }, [event, map]);
    return null;
};

const MapController = ({ setCreateEventCoords, eventsToDisplay, setSelectedEvent, user, onViewportChange }) => {
    const map = useMap();
    const timerRef = useRef(null);
    const hasLocatedRef = useRef(false);
    const locateButtonRef = useRef(null);
    const createHereButtonRef = useRef(null);

    useMapEvents({
        click(e) { setCreateEventCoords(e.latlng); },
        moveend() {
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => onViewportChange(map.getBounds()), 500);
        }
    });

    useEffect(() => {
        if (!hasLocatedRef.current) {
            map.locate({ setView: true, maxZoom: 14 });
            hasLocatedRef.current = true;
        }
        if (locateButtonRef.current) {
            L.DomEvent.disableClickPropagation(locateButtonRef.current);
        }
        if (createHereButtonRef.current) {
            L.DomEvent.disableClickPropagation(createHereButtonRef.current);
        }
        return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    }, [map]);

    const locateUser = (e) => {
        e.preventDefault();
        e.stopPropagation();
        map.locate({ setView: true, maxZoom: 14 });
    };

    const createEventHere = (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Event an aktueller GPS-Position erstellen
        map.locate();
        map.once('locationfound', (locationEvent) => {
            setCreateEventCoords(locationEvent.latlng);
        });
    };
    
    const getMarkerStyle = (event) => {
        let style = { fontSize: '2.5em', textShadow: '0 0 5px black', cursor: 'pointer' };
        if (!user || !event.participants) return style;
        const myParticipation = event.participants.find(p => p.user_id === user.id);
        if (event.user_id === user.id) { style.filter = 'drop-shadow(0 0 6px var(--color-gold-glow))'; }
        else if (myParticipation?.status === 'accepted') { style.filter = 'drop-shadow(0 0 5px var(--color-success-glow))'; }
        else if (myParticipation?.status === 'pending') { style.filter = 'drop-shadow(0 0 5px orange)'; }
        return style;
    };
    
    return (
        <>
            <a href="#" ref={locateButtonRef} title="My Location" role="button" onClick={locateUser} className="glass-effect" style={{position:'absolute', top: '100px', left: '15px', width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2em', color: '#fff', textDecoration: 'none', borderRadius: '50%', zIndex: 1001, cursor: 'pointer' }}>
                &#9678;
            </a>
            <a href="#" ref={createHereButtonRef} title="Create Event Here" role="button" onClick={createEventHere} className="glass-effect" style={{position:'absolute', top: '165px', left: '15px', width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5em', color: '#fff', textDecoration: 'none', borderRadius: '50%', zIndex: 1001, cursor: 'pointer', fontWeight: 'bold', lineHeight: '1' }}>
                +
            </a>
            {eventsToDisplay.map(event => (
                <Marker key={event.id} eventId={event.id} position={[event.latitude, event.longitude]} icon={L.divIcon({ html: `<div style='${Object.entries(getMarkerStyle(event)).map(([k, v]) => `${k.replace(/([A-Z])/g, '-$1').toLowerCase()}:${v};`).join('')}'>${sportTypes[event.sport_type]?.icon || '📍'}</div>`, className: 'emoji-marker', iconSize: [40, 40], iconAnchor: [20, 40] })} eventHandlers={{ click: () => setSelectedEvent(event) }} />
            ))}
        </>
    );
};

const StatusBarContent = ({ adItems }) => {
    const { t } = useTranslation();
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        if (adItems.length <= 1) return;
        const timer = setInterval(() => {
            setCurrentIndex(prevIndex => (prevIndex + 1) % adItems.length);
        }, 10000);
        return () => clearInterval(timer);
    }, [adItems.length]);

    if (!adItems || adItems.length === 0) return null;
    const currentAd = adItems[currentIndex];

    return (
        <AnimatePresence mode="wait">
            <motion.div key={currentIndex} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {currentAd.type === 'marquee' && <p className="marquee-text">{t(currentAd.textKey)}</p>}
                {currentAd.type === 'image' && <img src={currentAd.src} alt="ad" className="ad-image" />}
            </motion.div>
        </AnimatePresence>
    );
};

const MapPage = () => {
    const { t, i18n } = useTranslation();
    const { logout, user, updateUser } = useAuth();
    const { events, error: eventError, fetchEventsByBounds, setMapBounds, pendingRequestCount } = useEvents();
    const { notification } = useNotification();

    const [createEventCoords, setCreateEventCoords] = useState(null);
    const [showMyEvents, setShowMyEvents] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showChangeVibe, setShowChangeVibe] = useState(false);
    const [flyToEvent, setFlyToEvent] = useState(null);
    const [showAllVibes, setShowAllVibes] = useState(true); // Wird nur noch über ME-Modal gesteuert

    const { id: eventIdFromUrl } = useParams();
    const navigate = useNavigate();
    const defaultPosition = [51.16, 10.45];
    
    const filteredEvents = useMemo(() => {
        if (showAllVibes || !user?.favorite_sport) return events;
        return events.filter(event => event.sport_type === user.favorite_sport);
    }, [events, showAllVibes, user?.favorite_sport]);
    
    useEffect(() => {
        if (eventIdFromUrl && events.length > 0) {
            const eventToOpen = events.find(e => e.id.toString() === eventIdFromUrl);
            if (eventToOpen) { setSelectedEvent(eventToOpen); navigate('/', { replace: true }); }
        }
    }, [eventIdFromUrl, events, navigate]);

    const handleLogout = () => { logout(); };
    
    const cycleLanguage = () => {
        const languages = ['en', 'de', 'es', 'fr', 'it', 'pt', 'tr', 'ar', 'th', 'he', 'hi', 'zh', 'ja'];
        const currentLangIndex = languages.indexOf(i18n.language.split('-')[0]);
        const nextLang = languages[(currentLangIndex + 1) % languages.length];
        i18n.changeLanguage(nextLang);
    };

    const handleFlyToEvent = (event) => {
        setShowAllVibes(true);
        setFlyToEvent(event);
        setShowMyEvents(false);
    };

    const handleViewportChange = useCallback((bounds) => {
        setMapBounds(bounds);
        fetchEventsByBounds(bounds);
    }, [fetchEventsByBounds, setMapBounds]);
    
    const handleVibeChange = async (newVibe) => {
        try {
            await updateUser({ favorite_sport: newVibe });
            setShowChangeVibe(false);
        } catch (error) { console.error("Failed to update vibe", error); }
    };

    const handleOpenChangeVibe = () => {
        setShowMyEvents(false);
        setShowChangeVibe(true);
    };

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative', flexShrink: 0, zIndex: 1001 }}>
                <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'stretch', minHeight: '80px', paddingTop: 'env(safe-area-inset-top)', background: 'rgba(28, 28, 30, 0.7)', backdropFilter: 'blur(15px)', WebkitBackdropFilter: 'blur(15px)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <button onClick={() => setShowMyEvents(true)} style={{ position: 'relative', background: 'none', border: 'none', color: '#aaa', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', flex: 1, padding: '10px 0', fontSize: '1em' }}>
                        <img src={user?.selfie_path ? `/${user.selfie_path}` : ''} alt="me" style={{width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', border: '2px solid #555'}}/>
                        <span style={{ fontSize: '0.7em', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('me_button')}</span>
                        {pendingRequestCount > 0 && <div className="notification-badge"></div>}
                    </button>
                    <button onClick={() => setShowAllVibes(!showAllVibes)} style={{ background: 'none', border: 'none', color: '#aaa', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', flex: 1, padding: '10px 0', fontSize: '1em' }}>
                        <span style={{fontSize: '2.5em'}}>{showAllVibes ? '🌐' : (sportTypes[user?.favorite_sport]?.icon || '★')}</span>
                        <span style={{ fontSize: '0.7em', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{showAllVibes ? t('all_sports_button') : 'My Vibe'}</span>
                    </button>
                    <button onClick={cycleLanguage} style={{ background: 'none', border: 'none', color: '#aaa', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', flex: 1, padding: '10px 0', fontSize: '1em' }}>
                        <span style={{fontSize: '2em', fontWeight: 'bold'}}>{i18n.language.split('-')[0].toUpperCase()}</span>
                        <span style={{ fontSize: '0.7em', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('lang_button')}</span>
                    </button>
                    <button onClick={handleLogout} style={{ background: 'none', border: 'none', color: '#aaa', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', flex: 1, padding: '10px 0', fontSize: '1em' }}>
                        <span style={{fontSize: '2.5em'}}>⏏️</span>
                        <span style={{ fontSize: '0.7em', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('logout_button')}</span>
                    </button>
                </div>
            </div>
            
            <div style={{ flexGrow: 1, position: 'relative' }}>
                <MapContainer center={defaultPosition} zoom={6} style={{ height: '100%', width: '100%' }} zoomControl={false}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <MapController 
                        user={user} 
                        setCreateEventCoords={setCreateEventCoords} 
                        eventsToDisplay={filteredEvents} 
                        setSelectedEvent={setSelectedEvent} 
                        onViewportChange={handleViewportChange} 
                    />
                    <FlyToController event={flyToEvent} />
                </MapContainer>
                
                <div className="status-bar-bottom">
                    <AnimatePresence>
                        {notification ? (
                            <motion.div key="notification" initial={{ y: "100%", opacity: 0 }} animate={{ y: "0%", opacity: 1 }} exit={{ y: "100%", opacity: 0 }} transition={{ type: 'spring', stiffness: 100, damping: 20 }} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: '#ff3333', padding: '0 15px' }}>
                                <div className={notification.text.length > 50 ? 'marquee-scroll' : ''} style={{ color: '#fff', fontWeight: 'bold', textShadow: '0 2px 4px rgba(0,0,0,0.5)', whiteSpace: 'nowrap', fontSize: '1.1em' }}>
                                    {notification.text}
                                </div>
                            </motion.div>
                        ) : (
                            <StatusBarContent adItems={adItems} />
                        )}
                    </AnimatePresence>
                </div>

                {eventError && <div style={{position:'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 1001, background: 'rgba(255, 50, 50, 0.8)', color: 'white', padding: '10px 20px', borderRadius: '12px' }}>{eventError}</div>}
            </div>
            
            {createEventCoords && <EventCreationModal coords={createEventCoords} onClose={() => setCreateEventCoords(null)} />}
            {showMyEvents && <MyEventsModal
                onClose={() => setShowMyEvents(false)}
                onEventClick={handleFlyToEvent}
                onOpenChangeVibe={handleOpenChangeVibe}
            />}
            {selectedEvent && <EventDetails event={selectedEvent} onClose={() => setSelectedEvent(null)} />}
            {showChangeVibe && <ChangeVibeModal onClose={() => setShowChangeVibe(false)} onSave={handleVibeChange} />}

            <NotificationPrompt />
        </div>
    );
};

export default MapPage;
